// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductListProductImageStyles } from "@eisinc/Overrides/Widgets/ProductList/ProductListProductImage";
import { ProductListProductInformationStyles } from "@eisinc/Overrides/Widgets/ProductList/ProductListProductInformation";
import { HasIsLoading } from "@eisinc/XDL/types";
import { mergeWithCss } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ProductListProductCardStyles;
}

type Props = OwnProps & ProductCardSelections & HasProduct & HasIsLoading;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productListProductImage?: ProductListProductImageStyles;
    productListProductInformation?: ProductListProductInformationStyles;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const ProductListProductCard: FC<Props> = ({ isLoading, extendedStyles, ...props }) => {
    // <XNG-Changes>
    const [styles] = useState(() => mergeWithCss(productCardStyles, extendedStyles));
    // </XNG-Changes>
    return (
        // XNG-Changes : use StyledWrapper instead of using GridContainer & GridItem
        <StyledWrapper
            {...styles.gridContainer}
            data-test-selector={`productListProductCard${isLoading ? "" : props.product.id}`}
            // XNG-Changes : adding as="article" for semantic html purposes
            as="article"
        >
            <StyledWrapper {...styles.leftColumnGridItem}>
                {/* XNG-Changes : check isLoading */}
                {!isLoading && props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    // XNG-Changes : don't show compare here
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    // XNG-Changes
                    extendedStyles={styles.productListProductImage}
                    {...{ isLoading, view: "List" }}
                />
            </StyledWrapper>
            {/* XNG-Changes */}
            {/* Using "section" for semantic HTML, as "main" can't be used here due to another "main" element on the page. */}
            <StyledWrapper {...styles.rightColumnGridItem} as="section">
                {/* <StyledWrapper {...styles.rightColumnGridContainer}> */}
                {/* <StyledWrapper {...styles.infoGridItem}> */}
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={props.showAvailability}
                    showAttributes={false}
                    showImageBadges={props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                    // XNG-Changes
                    {...{ isLoading, extendedStyles: styles.productListProductInformation, showCustomBadges: true }}
                />
                {/* </StyledWrapper> */}
                {/* <StyledWrapper {...styles.actionsGridItem}>
                        <ProductListActions showPrice={props.showPrice} showAddToList={props.showAddToList} />
                    </StyledWrapper> */}
                {/* XNG-Changes : check isLoading */}
                {!isLoading && (
                    <StyledWrapper {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </StyledWrapper>
                )}
                {/* </StyledWrapper> */}
            </StyledWrapper>
            {/* XNG-Changes - Using "footer" for better semantic structure. */}
            <StyledWrapper {...styles.actionsGridItem} as="footer">
                <ProductListActions
                    showPrice={props.showPrice}
                    showAddToList={props.showAddToList}
                    // XNG-Changes
                    {...{ showCompare: props.showCompare, isLoading, showActions: true }}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
